import React, { useState, useEffect, useRef } from 'react';
import { predictionAPI } from '../services/api';

const FEATURE_META = {
  sr:          { label: 'Snoring Range',    unit: 'dB',   min: 40, max: 100, icon: '😴', desc: 'Sleep snoring intensity' },
  rr:          { label: 'Respiration Rate', unit: '/min', min: 12, max: 30,  icon: '🫁', desc: 'Breaths per minute' },
  t:           { label: 'Body Temp',        unit: '°F',   min: 96, max: 100, icon: '🌡️', desc: 'Core body temperature' },
  lm:          { label: 'Limb Movement',    unit: '/hr',  min: 5,  max: 45,  icon: '🦵', desc: 'Leg movements per hour' },
  bo:          { label: 'Blood Oxygen',     unit: '%',    min: 80, max: 100, icon: '🩸', desc: 'SpO2 saturation' },
  rem:         { label: 'Eye Movement',     unit: '%',    min: 40, max: 100, icon: '👁️', desc: 'REM sleep percentage' },
  sleep_hours: { label: 'Sleep Duration',   unit: 'hrs',  min: 3,  max: 10,  icon: '🌙', desc: 'Total sleep hours' },
  hr:          { label: 'Heart Rate',       unit: 'bpm',  min: 45, max: 100, icon: '❤️', desc: 'Resting heart rate' },
};

const DEMO_PRESETS = [
  { label: 'Low Stress',      sr:'42', rr:'15', t:'97.1', lm:'7',  bo:'98', rem:'96', sleep_hours:'8.2', hr:'59' },
  { label: 'Mild Stress',     sr:'55', rr:'17', t:'97.5', lm:'12', bo:'96', rem:'88', sleep_hours:'7.5', hr:'65' },
  { label: 'Moderate Stress', sr:'68', rr:'20', t:'98.0', lm:'18', bo:'93', rem:'75', sleep_hours:'6.5', hr:'72' },
  { label: 'High Stress',     sr:'82', rr:'24', t:'98.6', lm:'28', bo:'89', rem:'62', sleep_hours:'5.5', hr:'82' },
  { label: 'Critical Stress', sr:'95', rr:'28', t:'99.2', lm:'38', bo:'85', rem:'48', sleep_hours:'4.5', hr:'92' },
];

const RISK_CONFIG = {
  Low:      { color: '#10b981', bg: 'from-emerald-400 to-green-500',  emoji: '✅' },
  Mild:     { color: '#3b82f6', bg: 'from-blue-400 to-cyan-500',      emoji: '🔵' },
  Moderate: { color: '#f59e0b', bg: 'from-yellow-400 to-amber-500',   emoji: '⚠️' },
  High:     { color: '#f97316', bg: 'from-orange-400 to-red-400',     emoji: '🔴' },
  Critical: { color: '#ef4444', bg: 'from-red-500 to-rose-600',       emoji: '🚨' },
};

function useCounter(target, duration = 1200) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = target / (duration / 16);
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setVal(target); clearInterval(timer); }
      else setVal(Math.round(start * 10) / 10);
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return val;
}

function StressGauge({ score, risk }) {
  const [animated, setAnimated] = useState(0);
  const cfg = RISK_CONFIG[risk] || RISK_CONFIG.Low;
  const displayScore = useCounter(score);
  useEffect(() => { const t = setTimeout(() => setAnimated(score), 100); return () => clearTimeout(t); }, [score]);
  const circumference = Math.PI * 80;
  const progress = (animated / 100) * circumference;
  return (
    <div className="flex flex-col items-center">
      <div className="relative w-40 h-24 sm:w-52 sm:h-28">
        <svg viewBox="0 0 200 110" className="w-full h-full">
          <path d="M 20 95 A 80 80 0 0 1 180 95" fill="none" stroke="#1e293b" strokeWidth="16" strokeLinecap="round"/>
          {[['#10b981',0],['#3b82f6',0.2],['#f59e0b',0.4],['#f97316',0.6],['#ef4444',0.8]].map(([color, off], i) => (
            <path key={i} d="M 20 95 A 80 80 0 0 1 180 95" fill="none" stroke={color} strokeWidth="16"
              strokeLinecap="butt" strokeDasharray={`${circumference*0.2} ${circumference}`}
              strokeDashoffset={-circumference*off} opacity="0.3"/>
          ))}
          <path d="M 20 95 A 80 80 0 0 1 180 95" fill="none" stroke={cfg.color} strokeWidth="16"
            strokeLinecap="round" strokeDasharray={`${progress} ${circumference}`}
            style={{ transition: 'stroke-dasharray 1.2s cubic-bezier(0.4,0,0.2,1)' }}/>
          <g transform={`rotate(${(animated/100)*180-90} 100 95)`} style={{ transition: 'transform 1.2s cubic-bezier(0.4,0,0.2,1)' }}>
            <line x1="100" y1="95" x2="100" y2="28" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
            <circle cx="100" cy="95" r="6" fill="white"/>
            <circle cx="100" cy="95" r="3" fill={cfg.color}/>
          </g>
          <text x="18" y="108" fill="#64748b" fontSize="9" textAnchor="middle">0</text>
          <text x="100" y="14" fill="#64748b" fontSize="9" textAnchor="middle">50</text>
          <text x="182" y="108" fill="#64748b" fontSize="9" textAnchor="middle">100</text>
        </svg>
        <div className="absolute inset-0 flex items-end justify-center pb-1">
          <div className="text-center">
            <div className="text-2xl sm:text-4xl font-black text-white"
              style={{ textShadow: `0 0 12px ${cfg.color}, 0 2px 8px rgba(0,0,0,0.9)`, WebkitTextStroke: `1px ${cfg.color}` }}>
              {Math.round(displayScore)}
            </div>
            <div className="text-xs font-bold tracking-widest uppercase px-2 py-0.5 rounded-full"
              style={{ background: 'rgba(0,0,0,0.6)', color: cfg.color }}>Score</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImpactBar({ feature, value, impact, index }) {
  const [width, setWidth] = useState(0);
  const meta = FEATURE_META[feature] || { label: feature, unit: '', icon: '📊', desc: '' };
  const isPositive = impact > 0;
  const pct = Math.min(Math.abs(impact) / 0.15 * 100, 100);
  useEffect(() => { const t = setTimeout(() => setWidth(pct), 200 + index * 120); return () => clearTimeout(t); }, [pct, index]);
  return (
    <div className="p-3 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 transition-all duration-300 border border-slate-700/50">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-base flex-shrink-0">{meta.icon}</span>
          <div className="min-w-0">
            <div className="text-xs sm:text-sm font-semibold text-white truncate">{meta.label}</div>
            <div className="text-xs text-slate-400 hidden sm:block">{meta.desc}</div>
          </div>
        </div>
        <div className="text-right flex-shrink-0 ml-2">
          <div className="text-xs sm:text-sm font-bold text-white">{value}<span className="text-slate-400 text-xs ml-0.5">{meta.unit}</span></div>
          <div className={`text-xs font-bold ${isPositive ? 'text-red-400' : 'text-emerald-400'}`}>
            {isPositive ? '▲' : '▼'}{impact.toFixed(3)}
          </div>
        </div>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${isPositive ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-emerald-500 to-teal-400'}`}
          style={{ width: `${width}%` }}/>
      </div>
    </div>
  );
}

function MetricCard({ label, value, unit, color, icon, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  return (
    <div className={`rounded-2xl p-3 sm:p-4 border transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      style={{ background: `${color}15`, borderColor: `${color}40` }}>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-base sm:text-xl">{icon}</span>
        <span className="text-xs text-slate-400 font-medium uppercase tracking-wider truncate">{label}</span>
      </div>
      <div className="text-xl sm:text-3xl font-black" style={{ color }}>{value}</div>
      <div className="text-xs text-slate-500 mt-0.5 truncate">{unit}</div>
    </div>
  );
}

export default function Prediction() {
  const [formData, setFormData] = useState({ sr:'', rr:'', t:'', lm:'', bo:'', rem:'', sleep_hours:'', hr:'' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [demoIdx, setDemoIdx] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const resultRef = useRef(null);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleDemo = () => { setFormData(DEMO_PRESETS[demoIdx]); setDemoIdx((demoIdx + 1) % DEMO_PRESETS.length); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setShowResult(false);
    try {
      const data = Object.fromEntries(Object.entries(formData).map(([k, v]) => [k, parseFloat(v)]));
      const res = await predictionAPI.predict(data);
      setResult(res.data);
      setTimeout(() => { setShowResult(true); resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    } catch (err) {
      alert('Prediction failed: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  };

  const cfg = result ? (RISK_CONFIG[result.risk_category] || RISK_CONFIG.Low) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      {/* Header */}
      <div className="text-center py-6 sm:py-10 px-4">
        <div className="inline-flex items-center gap-2 bg-slate-800/60 border border-slate-700 rounded-2xl px-4 py-2 mb-3">
          <span className="text-lg sm:text-2xl">🧠</span>
          <span className="text-xs sm:text-sm font-semibold text-slate-300 tracking-widest uppercase">AI Stress Detection</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-black bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
          Stress Prediction
        </h1>
        <p className="text-slate-400 text-sm sm:text-lg max-w-xl mx-auto">
          Enter your biometric sleep data for real-time AI-powered stress analysis
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 pb-16 grid grid-cols-1 xl:grid-cols-2 gap-5 sm:gap-8">

        {/* INPUT FORM */}
        <div className="bg-slate-800/60 backdrop-blur border border-slate-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-8 shadow-2xl">
          <div className="flex items-center justify-between mb-5 sm:mb-8">
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-white">Biometric Input</h2>
              <p className="text-slate-400 text-xs sm:text-sm mt-0.5">Fill all 8 sleep parameters</p>
            </div>
            <button onClick={handleDemo}
              className="flex items-center gap-1.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl font-semibold text-xs sm:text-sm transition-all duration-200 shadow-lg hover:scale-105 active:scale-95">
              🎯 <span className="hidden sm:inline">Demo </span>{DEMO_PRESETS[demoIdx].label}
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:gap-4">
              {Object.entries(FEATURE_META).map(([key, meta]) => (
                <div key={key} className="group">
                  <label className="flex items-center gap-1 text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    <span>{meta.icon}</span>
                    <span className="truncate">{meta.label}</span>
                  </label>
                  <div className="relative">
                    <input type="number" step="0.1" name={key} value={formData[key]} onChange={handleChange} required
                      placeholder={`${meta.min}–${meta.max}`}
                      className="w-full bg-slate-900/80 border border-slate-600/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 rounded-xl px-3 py-2.5 text-white placeholder-slate-600 text-sm transition-all duration-200 outline-none"/>
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-600 hidden sm:block">{meta.unit}</div>
                  </div>
                  {formData[key] && (
                    <div className="mt-1 h-1 bg-slate-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(((parseFloat(formData[key]) - meta.min) / (meta.max - meta.min)) * 100, 100)}%` }}/>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 hover:from-blue-500 hover:via-purple-500 hover:to-pink-500 disabled:from-slate-600 disabled:via-slate-600 disabled:to-slate-600 text-white py-3 sm:py-4 rounded-2xl font-bold text-base sm:text-lg transition-all duration-300 shadow-xl hover:scale-[1.02] active:scale-[0.98]">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                  </svg>
                  Analyzing...
                </span>
              ) : '🔬 Predict Stress Level'}
            </button>
          </form>
        </div>

        {/* RESULTS */}
        <div ref={resultRef}>
          {!result && !loading && (
            <div className="bg-slate-800/40 border border-slate-700/30 rounded-2xl sm:rounded-3xl p-8 h-full flex flex-col items-center justify-center text-center min-h-64">
              <div className="text-6xl sm:text-8xl mb-4 animate-pulse">🧬</div>
              <h3 className="text-xl sm:text-2xl font-bold text-slate-300 mb-2">Awaiting Analysis</h3>
              <p className="text-slate-500 text-sm max-w-xs">Fill in your biometric data and click predict</p>
              <div className="mt-6 grid grid-cols-2 gap-2 w-full max-w-xs">
                {['Sleep Quality','Heart Health','Stress Index','Recovery Score'].map(l => (
                  <div key={l} className="bg-slate-700/40 rounded-xl p-2.5 text-xs text-slate-500 border border-slate-700/50">{l}</div>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl sm:rounded-3xl p-8 h-full flex flex-col items-center justify-center text-center min-h-64">
              <div className="relative w-24 h-24 sm:w-32 sm:h-32 mb-5">
                {[0,1,2].map(i => <div key={i} className="absolute inset-0 rounded-full border-4 border-blue-500/20 animate-ping" style={{ animationDelay: `${i*0.2}s`, inset: `${i*8}px` }}/>)}
                <div className="absolute inset-0 flex items-center justify-center text-4xl sm:text-5xl">🧠</div>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">AI Processing...</h3>
              <p className="text-slate-400 text-xs sm:text-sm">Running ML models</p>
              <div className="mt-4 flex gap-1">
                {[0,1,2,3,4].map(i => <div key={i} className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: `${i*0.1}s` }}/>)}
              </div>
            </div>
          )}

          {result && showResult && (
            <div className="space-y-4">
              {/* Risk Banner */}
              <div className={`bg-gradient-to-r ${cfg.bg} rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-2xl`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-white/80 text-xs font-semibold uppercase tracking-widest mb-1">Risk Assessment</div>
                    <div className="text-2xl sm:text-4xl font-black text-white flex items-center gap-2">
                      {cfg.emoji} {result.risk_category}
                    </div>
                    <div className="text-white/70 text-xs sm:text-sm mt-1">Stress Level {result.stress_level} / 4</div>
                  </div>
                  <div className="bg-black/30 rounded-2xl p-2 backdrop-blur flex-shrink-0">
                    <StressGauge score={result.stress_score} risk={result.risk_category}/>
                  </div>
                </div>
              </div>

              {/* Metric Cards */}
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                <MetricCard label="Score"      value={Math.round(result.stress_score)} unit="out of 100"    color={cfg.color}  icon="📊" delay={0}/>
                <MetricCard label="Confidence" value={`${result.probability}%`}        unit="certainty"     color="#8b5cf6"    icon="🎯" delay={100}/>
                <MetricCard label="Level"      value={`${result.stress_level}/4`}      unit="severity"      color="#06b6d4"    icon="⚡" delay={200}/>
              </div>

              {/* Feature Impact */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-xs sm:text-sm">🔍</div>
                  <div>
                    <h3 className="font-bold text-white text-sm sm:text-base">Feature Impact</h3>
                    <p className="text-xs text-slate-400 hidden sm:block">How each biometric affects your stress</p>
                  </div>
                </div>
                <div className="space-y-2">
                  {result.shap_explanation.map((item, i) => (
                    <ImpactBar key={i} feature={item.feature} value={item.value} impact={item.impact} index={i}/>
                  ))}
                </div>
                <div className="mt-3 flex gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-gradient-to-r from-orange-500 to-red-500 inline-block"/>Increases</span>
                  <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-gradient-to-r from-emerald-500 to-teal-400 inline-block"/>Reduces</span>
                </div>
              </div>

              {/* Recommendations */}
              <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6">
                <h3 className="font-bold text-white mb-3 flex items-center gap-2 text-sm sm:text-base">💡 AI Recommendations</h3>
                <div className="space-y-2">
                  {result.stress_level >= 3 && <div className="flex gap-2 p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-xs sm:text-sm text-red-300"><span>🚨</span><span>Immediate stress intervention recommended.</span></div>}
                  {parseFloat(formData.sleep_hours) < 6 && <div className="flex gap-2 p-3 bg-orange-500/10 border border-orange-500/20 rounded-xl text-xs sm:text-sm text-orange-300"><span>🌙</span><span>Increase sleep to 7–8 hours.</span></div>}
                  {parseFloat(formData.hr) > 80 && <div className="flex gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-xs sm:text-sm text-yellow-300"><span>❤️</span><span>Elevated heart rate. Try cardio or breathing exercises.</span></div>}
                  {parseFloat(formData.bo) < 92 && <div className="flex gap-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-xs sm:text-sm text-blue-300"><span>🩸</span><span>Low blood oxygen. Practice deep breathing daily.</span></div>}
                  {result.stress_level <= 1 && <div className="flex gap-2 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-xs sm:text-sm text-emerald-300"><span>✅</span><span>Excellent metrics! Keep your sleep routine.</span></div>}
                  {result.stress_level === 2 && <div className="flex gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-xl text-xs sm:text-sm text-yellow-300"><span>⚠️</span><span>Moderate stress. Consider meditation.</span></div>}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
